import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, Payout as IPayout } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { StatusBadge } from './CheckoutSessions'
import { camelCase } from 'lodash'
import { errorToast, shortAddress, successToast, trimZeros } from '../util/Util'
import TimeText from '../components/TimeText'
import Table from '../components/Table'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import LinkExternal from '../components/LinkExternal'
import { getMethod, ManagedPaymentMethod } from '../util/Managed'
import { SectionContent, SectionItem, SubTitle } from '../components/Shared'

const Payout = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [payout, setPayout] = useState<IPayout>()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [customers, setCustomers] = useState<Customer[]>([])

  useEffect(() => {
    if (!params.id) return
    Radom.getPayout(params.id).then(p => {
      setPayout(p)
      if (p.payouts[0].payoutType.crypto) {
        setPaymentMethod(getMethod(p.payouts[0].payoutType.crypto.network, p.payouts[0].payoutType.crypto.token))
      }
    }).finally(() => setIsLoading(false))
  }, [params.id])

  useEffect(() => {
    if (!payout) return
    const ids = Array.from(
      new Set(
        payout.payouts
          .map(it => it.payoutType.crypto?.customerId)
          .filter((id): id is string => Boolean(id))
      )
    )
    Promise.all(ids.map(async id => await Radom.getCustomer(id)))
      .then(customers => setCustomers(customers))
      .catch(e => console.log(e))
  }, [payout])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (payout && paymentMethod) {
    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: payout.id, to: `/payout/${payout.id}?source=transactions` }
            ]
            : [
              { item: 'Payouts', to: '/payouts' },
              { item: payout.id, to: '.' }
            ]} />
      </div>
      <div style={{
        padding: 30,
        borderRadius: 10,
        border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: '32px'
        }}>
          <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
          <span>{trimZeros(payout.totalPayoutUsd)}</span>
          <span style={{
            background: RADOM_COLORS.GRAY_MED,
            color: RADOM_COLORS.BLACK,
            borderRadius: 10,
            fontSize: 14,
            padding: '5px 10px',
            fontWeight: 400
          }}>{paymentMethod.ticker}</span>
        </div>

        <SectionContent>
          <SectionItem>
            <SubTitle>Network fee</SubTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
              <span>{payout.estimatedFeeUsd}</span>
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Status</SubTitle>
            <StatusBadge status={camelCase(payout.status) as 'awaitingConfirmation' | 'pending' | 'complete' | 'expired' | 'processing'}>{payout.status}</StatusBadge>
          </SectionItem>

          <SectionItem>
            <SubTitle>Deduct fees</SubTitle>
            <span>{payout.deductFees ? 'Yes' : 'No'}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Email receipt</SubTitle>
            <span>{payout.sendEmailReceipts ? 'Yes' : 'No'}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>
              Created
            </SubTitle>
            <TimeText date={new Date(payout.createdAt)} />
          </SectionItem>

          <SectionItem>
            <SubTitle>
              Completed
            </SubTitle>
            <TimeText date={new Date(payout.completedAt)} />
          </SectionItem>
        </SectionContent>
      </div>

      <div style={{
        padding: 30,
        borderRadius: 10,
        border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
        <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Withdrawal transactions</SubTitle>
        <Table
          disableHoverStyle
          headers={[
            <span key="address">Payout address</span>,
            <span key="amount">Amount</span>,
            <span key="network">Network</span>,
            <span key="name">Beneficiary Name</span>,
            <span key="email">Beneficiary Email</span>,
            <span key="hash">Transaction Hash</span>
          ]}
          items={payout.payouts.map((item, i) => ([
            <div key={`${i}-a`} style={{
              display: 'flex',
              gap: 5,
              alignItems: 'center'
            }}>
              <span>{shortAddress(item.payoutType.crypto?.payoutAddress ?? '', 8)}</span>
              <IconButton style={{ padding: 0 }}>
                <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard
                    .writeText(item.payoutType.crypto?.payoutAddress || '')
                    .then(() => {
                      successToast(
                        'Copied payout address'
                      )
                    })
                    .catch((err) => {
                      console.error('copy failed', err)
                      errorToast(
                        'Failed to copy payout address'
                      )
                    })
                }} />
              </IconButton>
            </div>,
            <div key={`${i}-b`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <img src={paymentMethod.logo} style={{ height: 18, width: 18 }} />
                <span>{trimZeros(item.payoutType.crypto?.amount || '')}</span>
              </div>
            </div>,
            <div key={`${i}-c`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <img src={paymentMethod.hostChain?.logo ?? paymentMethod.logo} style={{ height: 18, width: 18 }} />
                <span>
                  { paymentMethod.hostChain?.name ?? paymentMethod.name }
                </span>
              </div>
            </div>,
            <div key={`${i}-d`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>
                  {customers.find(c => c.id === item.payoutType.crypto?.customerId)?.name ?? '-'}
                </span>
                {customers.find(c => c.id === item.payoutType.crypto?.customerId) &&
                <IconButton style={{ padding: 0 }}>
                  <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(customers.find(c => c.id === item.payoutType.crypto?.customerId)?.name ?? '')
                      .then(() => {
                        successToast(
                          'Copied customer name'
                        )
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast(
                          'Failed to copy customer name'
                        )
                      })
                  }} />
                </IconButton>}
              </div>
            </div>,
            <div key={`${i}-e`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>
                  {customers.find(c => c.id === item.payoutType.crypto?.customerId)?.email ?? '-'}
                </span>
                {customers.find(c => c.id === item.payoutType.crypto?.customerId) &&
                <IconButton style={{ padding: 0 }}>
                  <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(customers.find(c => c.id === item.payoutType.crypto?.customerId)?.email ?? '')
                      .then(() => {
                        successToast(
                          'Copied customer email'
                        )
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast(
                          'Failed to copy customer email'
                        )
                      })
                  }} />
                </IconButton>}
              </div>
            </div>,
            <div key={`${i}-f`}>
              <ExternalLinkText
                style={
                  { textDecoration: 'underline' }
                }
                onClick={e => e.stopPropagation()}
                target="_blank" rel="noreferrer"
                href={formatExplorerUrl(
                  item.payoutType.crypto?.network || '',
                  'tx',
                  item.payoutEntryReceipt?.crypto?.txHash || ''
                )}>
                {shortAddress(item.payoutEntryReceipt?.crypto?.txHash ?? '', 8)}
                <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
              </ExternalLinkText>
            </div>
          ]))}
          isLoading={false} />
      </div>
    </Container>
  }

  return <div>
    <span>Payout not found</span>
  </div>
})

export default Payout
